import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import { sendProductRecommendationTracking } from "@insite/client-framework/Services/ProductRecommendationsService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeModel, ProductModel, SpecificationModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { ProductQuantityBreakPricingStyles } from "@insite/content-library/Components/ProductQuantityBreakPricing";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import ChevronsRight from "@insite/mobius/Icons/ChevronsRight";
import Link from "@insite/mobius/Link";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { customStyles } from "@sonic/Assets/styles/CustomStyles";
import { ProductLineDisplay, tooltipStyle } from "@sonic/CustomComponents/ProductLineDisplay";
import ProductImage, { ProductImageStyles } from "@sonic/Overrides/Components/ProductImage";
import ProductPrice, { ProductPriceStyles } from "@sonic/Overrides/Components/ProductPrice";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    productState: getProductState(state, ownProps.product.id).value,
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: ProductCarouselProductStyles;
}

export interface MappedAttributes {
    name: string;
    value: string;
}

interface RequiresQuoteMessageStyle {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    quoteMessage?: RequiresQuoteMessageStyle;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    productAttributesWrapper?: InjectableCss;
    showOnHoverContainer?: InjectableCss;
    productFooterWrapper?: InjectableCss;
    wrapper?: InjectableCss;
    expertiseStyle?: InjectableCss;
    productPriceStyles?: ProductPriceStyles;
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productBrandWrapper: {
        css: css`
            margin: 6px 0;
            min-height: 22px;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            typographyProps: { size: 25, fontFamily: "Pancetta W05 SemiBold" },
            css: css`
                color: #000;
                font-size: 25px !important;
                margin-top: 10px;
                min-height: 2rem;
                text-align: left;

                overflow: hidden;
                text-overflow: ellipsis;

                /* display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical; */
                -webkit-line-clamp: 2;
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 1;
                margin-top: 10px;
                display: flex;
                align-items: flex-end;
            `,
        },
        erpNumberLabelText: { color: "#666666", size: 13 },
        erpNumberValueText: { color: "#666666", size: 13 },
    },
    productPriceWrapper: {
        css: css`
            margin-left: auto;
            flex-grow: 0;
            margin-bottom: 6px;
            display: flex;
            align-items: flex-end;
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 6px;
            `,
        },
    },
    addToCartContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            width: calc(100% + 20px);
            margin-left: -10px;
        `,
    },
    qtyGridItem: {
        width: 3,
    },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
                margin-right: 0;
            `,
            inputSelect: css`
                border-top: 1px solid #626262;
                border-right: 1px solid #626262;
                border-bottom: none;
                border-left: none;
                text-align: center;
                font-weight: 700;
            `,
        },
    },
    addToCartButtonGridItem: {
        width: 9,
    },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: { size: 14, weight: 600 },
            css: css`
                text-align: center;
                justify-content: center;
                margin-top: 10px;
            `,
        },
    },
    productAttributesWrapper: {
        css: css`
            color: ${getColor("text.disabled")};
            margin-top: auto;
            margin-bottom: 10px;
            justify-self: flex-end;
            display: flex;
            align-items: center;
            justify-content: space-between;
        `,
    },
    showOnHoverContainer: {
        css: css`
            padding-bottom: 10px;
            transform: translateY(-150px);
        `,
    },
    productFooterWrapper: {
        css: css`
            border-top: 1px solid #d8d8d8;
            padding-top: 10px;
            bottom: 0;
            width: 100%;
            display: flex;
            flex-wrap: wrap;
            margin-top: auto;
        `,
    },
    wrapper: {
        css: css`
            position: relative;
            height: 400px;
            display: flex;
            flex-direction: column;
            padding: 10px 10px 0 10px;
            border-radius: 3px;
            box-shadow: 0 0 4px 0 #00000040;
        `,
    },
    quoteMessage: {
        text: {
            css: css`
                display: flex;
                align-items: center;
                margin-right: 10px;
            `,
        },
    },
    productPriceStyles: {
        price: {
            priceText: {
                size: 18,
                color: "text.main",
                fontFamily: "Pancetta W05 SemiBold",
                css: css`
                    ${({ theme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                font-size: 18px;
                            `,
                            null,
                            null,
                            null,
                            null,
                        ])};
                    white-space: nowrap;
                    width: 100%;
                `,
            },
        },
    },
};

const ProductCarouselProduct: React.FC<Props> = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    productState,
}) => {
    const [styles] = React.useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));
    const [showAttributes, setshowAttributes] = React.useState(false);
    const [hasAttributes, setAttributes] = React.useState<AttributeTypeModel[]>([]);
    const [hasSpecifications, setSpecifications] = React.useState<SpecificationModel[]>([]);
    const [mappedAttributes, setMappedAttsAndSpecs] = React.useState<MappedAttributes[]>([]);
    const mountedStyle = { height: 0, opacity: 1, transition: "opacity 500ms ease-in" };
    const unmountedStyle = { height: 0, opacity: 0, transition: "opacity 200ms ease-in" };

    const attributeSpecificationTransformer = (atts: AttributeTypeModel[], specs: SpecificationModel[]) => {
        const mappedAtts = atts.map(att => {
            return {
                name: att.label || att.name,
                value: att.attributeValues!.map(attributeValue => attributeValue.valueDisplay).join(", "),
            };
        });
        const mappedSpecs = specs.map(spec => {
            return {
                name: spec.nameDisplay || spec.name,
                value: spec.value,
            };
        });
        return [...mappedAtts, ...mappedSpecs];
    };

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const expertiseStyle = {
        css: css`
            font-size: 18px;
            line-height: 21.6px;
            border-radius: 6px;
            margin-left: auto;
            color: #fff;
            width: 35px;
            padding: 5px 0;
            text-align: center;
        `,
    };

    React.useEffect(() => {
        if (!productState) {
            return;
        }

        if (productState.attributeTypes || productState.specifications) {
            const list = [
                "TypeFilling",
                "DrawerQuantity",
                "SizeVariant",
                "Cable Length",
                "Cranking Amps",
                "CapacityTon",
                "Min-Max height",
                "H",
                "L",
                "W",
                "WeigthG",
            ];
            const sorted = productState?.attributeTypes?.filter(item => list.includes(item.name));
            const sortedSpec = productState?.specifications?.filter(item => list.includes(item.name));
            setSpecifications(sortedSpec || []);
            setAttributes(sorted || []);
        }
    }, [productState?.attributeTypes?.length, productState?.specifications?.length]);

    React.useEffect(() => {
        const atts = hasAttributes || [];
        const specs = hasSpecifications || [];
        setMappedAttsAndSpecs(attributeSpecificationTransformer(atts, specs));
    }, [hasAttributes, hasSpecifications]);

    const translateUp = "transform: translateY(-170px);";
    const quoteStyles = styles.quoteMessage || {};
    const tooltipText = translate(
        "Expertise Level is an indicator of the necessary familiarity you should have with the subject matter (Basic, Beginner, Intermediate and Advanced)",
    );

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    return (
        <>
            <ProductContext.Provider value={productContext}>
                <StyledWrapper
                    onMouseLeave={() => setshowAttributes(false)}
                    onMouseEnter={() => setshowAttributes(true)}
                    {...styles.wrapper}
                >
                    <StyledWrapper
                        css={css`
                            display: flex;
                            flex-direction: column;
                        `}
                    >
                        <StyledWrapper
                            css={css`
                                transition: transform 0.2s ease;
                                ${showAttributes ? translateUp : ""}
                            `}
                        >
                            {showImage && (
                                <ProductImage
                                    animateOpacity={showAttributes}
                                    product={productContext}
                                    extendedStyles={styles.productImageStyles}
                                />
                            )}
                            {showPartNumbers && (
                                <ProductPartNumbers
                                    productNumber={product.productNumber}
                                    customerProductNumber={product.customerProductNumber}
                                    manufacturerItem={product.manufacturerItem}
                                    showCustomerName={false}
                                    showManufacturerItem={false}
                                    extendedStyles={styles.productPartNumbersStyles}
                                    showLabel={false}
                                />
                            )}
                            {showTitle && (
                                <ProductDescription
                                    product={productContext}
                                    extendedStyles={styles.productDescriptionStyles}
                                />
                            )}
                        </StyledWrapper>

                        <StyledWrapper
                            style={showAttributes ? mountedStyle : unmountedStyle}
                            {...styles.showOnHoverContainer}
                        >
                            {product.productLine && (
                                <StyledWrapper {...styles.productAttributesWrapper}>
                                    <Typography ellipsis {...quoteStyles.text}>
                                        {translate("Expertise level")}:&nbsp;
                                        <Tooltip
                                            cssOverrides={{ tooltipWrapper: tooltipStyle.tooltipWrapper }}
                                            typographyProps={{ weight: 400 }}
                                            text={tooltipText}
                                        />
                                    </Typography>
                                    <ProductLineDisplay variant="card-right" product={productContext.product} />
                                </StyledWrapper>
                            )}
                            {mappedAttributes.map((att, i) => {
                                const showAttributes = product.productLine ? 2 : 3;
                                if (i <= showAttributes) {
                                    return (
                                        <StyledWrapper {...styles.productAttributesWrapper} key={att.name}>
                                            <Typography size={18}>{att.name}:&nbsp;</Typography>
                                            <Typography>{att.value}</Typography>
                                        </StyledWrapper>
                                    );
                                }
                                return null;
                            })}
                        </StyledWrapper>
                    </StyledWrapper>

                    <StyledWrapper {...styles.productFooterWrapper}>
                        <Link
                            icon={{ iconProps: { src: ChevronsRight, color: "#e30613" }, position: "right" }}
                            {...customStyles.sonicLinkStyle}
                            href={product.canonicalUrl}
                        >
                            {translate("More information")}
                        </Link>
                        {showPrice && (
                            <StyledWrapper {...styles.productPriceWrapper}>
                                <VisuallyHidden as="label" htmlFor={`${carouselId}-uom`} id={`${carouselId}-uom-label`}>
                                    {translate("U/M")}
                                </VisuallyHidden>
                                <ProductPrice
                                    extendedStyles={productCarouselProductStyles.productPriceStyles}
                                    showLabel={false}
                                    product={productContext}
                                />
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
